import { batch, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import type { DraftTask, Priority, Project, SyncStatus, Task, Theme, ViewKey } from "./types";
import { getAll, putLocal, putMany, readQueue, removeLocal } from "./db";
import { enqueue, flushQueue, pullRemote } from "./sync";
import { cleanColor, cleanNotes, cleanText, createProject, createTask, isUpcoming, MAX_NOTES, MAX_PROJECT_NAME, MAX_TITLE, nowIso, todayKey } from "./utils";

interface DeletedSnapshot {
  task: Task;
  expires: number;
}

export function createTodoStore() {
  const [userId, setUserId] = createSignal<string | null>(null);
  const [tasks, setTasks] = createSignal<Task[]>([]);
  const [projects, setProjects] = createSignal<Project[]>([]);
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [view, setView] = createSignal<ViewKey>("today");
  const [search, setSearch] = createSignal("");
  const [projectFilter, setProjectFilter] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [theme, setTheme] = createSignal<Theme>((localStorage.getItem("theme") as Theme | null) ?? "light");
  const [lastDeleted, setLastDeleted] = createSignal<DeletedSnapshot | null>(null);
  const [syncStatus, setSyncStatus] = createSignal<SyncStatus>({
    online: navigator.onLine,
    syncing: false,
    pending: 0,
    error: null,
    nextRetryAt: null,
  });

  const visibleTasks = createMemo(() => {
    const q = search().trim().toLowerCase();
    const today = todayKey();
    return tasks()
      .filter((task) => !task.deleted_at)
      .filter((task) => (projectFilter() ? task.project_id === projectFilter() : true))
      .filter((task) => {
        if (view() === "today") return task.due_date === today && !task.completed;
        if (view() === "upcoming") return isUpcoming(task.due_date) && !task.completed;
        if (view() === "completed") return task.completed;
        return !task.completed;
      })
      .filter((task) => {
        if (!q) return true;
        return `${task.title} ${task.notes}`.toLowerCase().includes(q);
      })
      .sort((a, b) => a.position - b.position || b.updated_at.localeCompare(a.updated_at));
  });

  function patchStatus(patch: Partial<SyncStatus>) {
    setSyncStatus((current) => ({ ...current, ...patch }));
  }

  async function load(nextUserId: string | null, syncRemote = true) {
    setUserId(nextUserId);
    setLoading(true);
    const [localTasks, localProjects, queue] = await Promise.all([getAll("tasks"), getAll("projects"), readQueue()]);
    batch(() => {
      setTasks(localTasks.filter((task) => !nextUserId || task.user_id === nextUserId));
      setProjects(localProjects.filter((project) => !nextUserId || project.user_id === nextUserId).sort((a, b) => a.position - b.position));
      patchStatus({ pending: queue.length, online: navigator.onLine });
      setLoading(false);
    });
    if (nextUserId && syncRemote) {
      syncNow();
      void pullRemote(nextUserId)
        .then((remote) => {
          batch(() => {
            const removed = new Set(remote.removedProjectIds);
            setTasks((current) =>
              mergeRows(current, remote.tasks).map((task) => (task.project_id && removed.has(task.project_id) ? { ...task, project_id: null } : task)),
            );
            setProjects((current) =>
              mergeRows(current, remote.projects)
                .filter((project) => !removed.has(project.id))
                .sort((a, b) => a.position - b.position),
            );
            if (projectFilter() && removed.has(projectFilter()!)) setProjectFilter(null);
          });
        })
        .catch((error) => patchStatus({ error: error instanceof Error ? error.message : "Could not load remote changes" }));
    }
  }

  async function persistTask(task: Task) {
    setTasks((current) => {
      const index = current.findIndex((item) => item.id === task.id);
      if (index === -1) return [task, ...current];
      const next = current.slice();
      next[index] = task;
      return next;
    });
    await putLocal("tasks", task);
    await enqueue("tasks", "upsert", task.id, task);
    void syncNow();
  }

  async function addTask(draft: DraftTask) {
    const owner = userId();
    const title = cleanText(draft.title, MAX_TITLE);
    if (!owner || !title) return;
    const firstPosition = tasks()
      .filter((task) => !task.deleted_at)
      .reduce((minimum, task) => Math.min(minimum, task.position), 0);
    const task = createTask(owner, { ...draft, title }, firstPosition - 1);
    setSelectedId(task.id);
    await persistTask(task);
  }

  async function updateTask(id: string, patch: Partial<Pick<Task, "title" | "notes" | "completed" | "priority" | "due_date" | "project_id" | "position">>) {
    const task = tasks().find((item) => item.id === id);
    if (!task) return;
    const next: Task = {
      ...task,
      ...patch,
      title: patch.title === undefined ? task.title : cleanText(patch.title, MAX_TITLE),
      notes: patch.notes === undefined ? task.notes : cleanNotes(patch.notes).slice(0, MAX_NOTES),
      priority: (patch.priority ?? task.priority) as Priority,
      updated_at: nowIso(),
    };
    if (!next.title) return;
    await persistTask(next);
  }

  async function deleteTask(id: string) {
    const task = tasks().find((item) => item.id === id);
    if (!task) return;
    const deleted_at = nowIso();
    const next = { ...task, deleted_at, updated_at: deleted_at };
    setLastDeleted({ task, expires: Date.now() + 7000 });
    setTasks((current) => current.map((item) => (item.id === id ? next : item)));
    await putLocal("tasks", next);
    await enqueue("tasks", "delete", id, { id, deleted_at, updated_at: deleted_at });
    void syncNow();
  }

  async function undoDelete() {
    const snapshot = lastDeleted();
    if (!snapshot || snapshot.expires < Date.now()) return;
    setLastDeleted(null);
    await persistTask({ ...snapshot.task, deleted_at: null, updated_at: nowIso() });
  }

  async function addProject(name: string) {
    const owner = userId();
    const clean = cleanText(name, MAX_PROJECT_NAME);
    if (!owner || !clean) return;
    const project = createProject(owner, clean, projects().length + 1);
    setProjects((current) => [...current, project]);
    await putLocal("projects", project);
    await enqueue("projects", "upsert", project.id, project);
    void syncNow();
  }

  async function updateProject(id: string, patch: Partial<Pick<Project, "name" | "color">>) {
    const project = projects().find((item) => item.id === id);
    if (!project) return;
    const next: Project = {
      ...project,
      ...patch,
      name: patch.name === undefined ? project.name : cleanText(patch.name, MAX_PROJECT_NAME),
      color: patch.color === undefined ? project.color : cleanColor(patch.color),
      updated_at: nowIso(),
    };
    if (!next.name) return;
    setProjects((current) => current.map((item) => (item.id === id ? next : item)));
    await putLocal("projects", next);
    await enqueue("projects", "upsert", next.id, next);
    void syncNow();
  }

  async function deleteProject(id: string) {
    const project = projects().find((item) => item.id === id);
    if (!project) return;
    const timestamp = nowIso();
    const changedTasks = tasks()
      .filter((task) => task.project_id === id)
      .map((task) => ({ ...task, project_id: null, updated_at: timestamp }));

    setProjects((current) => current.filter((item) => item.id !== id));
    setTasks((current) => current.map((task) => (task.project_id === id ? { ...task, project_id: null, updated_at: timestamp } : task)));
    if (projectFilter() === id) setProjectFilter(null);

    await removeLocal("projects", id);
    if (changedTasks.length) {
      await putMany("tasks", changedTasks);
      await Promise.all(changedTasks.map((task) => enqueue("tasks", "upsert", task.id, task)));
    }
    await enqueue("projects", "delete", id, { id });
    void syncNow();
  }

  async function reorderTask(id: string, direction: -1 | 1) {
    const list = visibleTasks();
    const index = list.findIndex((task) => task.id === id);
    const swap = list[index + direction];
    const task = list[index];
    if (!task || !swap) return;
    await putMany("tasks", [
      { ...task, position: swap.position, updated_at: nowIso() },
      { ...swap, position: task.position, updated_at: nowIso() },
    ]);
    setTasks((current) =>
      current.map((item) => {
        if (item.id === task.id) return { ...item, position: swap.position, updated_at: nowIso() };
        if (item.id === swap.id) return { ...item, position: task.position, updated_at: nowIso() };
        return item;
      }),
    );
    await enqueue("tasks", "upsert", task.id, { ...task, position: swap.position, updated_at: nowIso() });
    await enqueue("tasks", "upsert", swap.id, { ...swap, position: task.position, updated_at: nowIso() });
    void syncNow();
  }

  async function syncNow(force = false) {
    patchStatus({ online: navigator.onLine });
    await flushQueue(patchStatus, { force });
  }

  function moveSelection(delta: number) {
    const list = visibleTasks();
    if (!list.length) return;
    const index = Math.max(0, list.findIndex((task) => task.id === selectedId()));
    const next = list[Math.min(list.length - 1, Math.max(0, index + delta))];
    setSelectedId(next.id);
  }

  function applyTheme(next: Theme) {
    setTheme(next);
    localStorage.setItem("theme", next);
    document.documentElement.dataset.theme = next;
  }

  window.addEventListener("online", () => {
    patchStatus({ online: true });
    void syncNow();
  });
  window.addEventListener("offline", () => patchStatus({ online: false }));
  createEffect(() => {
    const nextRetryAt = syncStatus().nextRetryAt;
    if (!nextRetryAt || !syncStatus().online) return;
    const delay = Math.max(0, Date.parse(nextRetryAt) - Date.now());
    const timer = window.setTimeout(() => void syncNow(), delay);
    onCleanup(() => window.clearTimeout(timer));
  });
  applyTheme(theme());

  return {
    userId,
    tasks,
    projects,
    selectedId,
    setSelectedId,
    view,
    setView,
    search,
    setSearch,
    projectFilter,
    setProjectFilter,
    loading,
    theme,
    applyTheme,
    syncStatus,
    visibleTasks,
    lastDeleted,
    load,
    addTask,
    updateTask,
    deleteTask,
    undoDelete,
    addProject,
    updateProject,
    deleteProject,
    reorderTask,
    syncNow,
    moveSelection,
  };
}

export type TodoStore = ReturnType<typeof createTodoStore>;

function mergeRows<T extends { id: string; updated_at: string }>(local: T[], remote: T[]): T[] {
  const rows = new Map(local.map((row) => [row.id, row]));
  for (const row of remote) {
    const current = rows.get(row.id);
    if (!current || row.updated_at >= current.updated_at) rows.set(row.id, row);
  }
  return Array.from(rows.values());
}
